import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import {
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import {
  iHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';

interface MultiLineInputItemProperties {
  text: string;
  stringInput: string;
  inputCaptionLeft: string;
  inputCaptionRight: string;
  maxTextLength: string;
  style: string;
}

@Component({
  selector: 'lfe-multi-line-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CdkTextareaAutosize],
  templateUrl: './multi-line-input-item-form.component.html',
  standalone: true,
  styleUrl: './multi-line-input-item-form.component.css'
})
export class MultiLineInputItemFormComponent
  extends iHasInputColorControls(InteractiveItemComponent)<MultiLineInputItemProperties>
  implements OnInit
{
  formControl: FormControl<string | null> | undefined;

  properties: InteractiveItemProperties & MultiLineInputItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    stringInput: '',
    inputCaptionLeft: '',
    inputCaptionRight: '',
    maxTextLength: '',
    style: 'Normal'
  };

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `multi_line_input_${this.currentId()}`);
  maxTextLength = computed(() => {
    const maxLength = this.properties.maxTextLength;
    return maxLength && Number(maxLength) ? Number(maxLength) : Number.MAX_SAFE_INTEGER;
  });

  currentTextColor = signal('');

  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));
  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  initialValue = '';

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.stringInput || '';

    // Build validators array
    const validators = [];
    const maxLength = this.maxTextLength();
    if (maxLength && maxLength !== Number.MAX_SAFE_INTEGER) {
      validators.push(Validators.maxLength(maxLength));
    }

    this.formControl = new FormControl(this.initialValue, validators);
    this.form().addControl(this.formControlId, this.formControl);
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & MultiLineInputItemProperties {
    const props = getPropsFrom(xml);

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));
    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      customId: props['CustomID'],
      activationRule: props['ActivationRule'],
      resultValue: props['ResultValue'],
      resultUser: props['ResultUser'],
      resultTime: props['ResultTime'],
      text: props['Text'],
      textBgColor,
      inputColor,
      stringInput: props['StringInput'],
      inputCaptionLeft: props['InputCaptionLeft'],
      inputCaptionRight: props['InputCaptionRight'],
      maxTextLength: props['MaxTextLength'],
      style: props['Style'] || 'Normal',
      isGenericRepeatItem
    };
  }

  onChange(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;

    this.properties.stringInput = value;
    this.properties.resultValue = value;
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.stringInput !== this.initialValue) {
      upsertChildren(element, {
        StringInput: this.properties.stringInput
      });
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }
}
